import React, { useEffect, useRef, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, Loader2, Maximize2, Upload } from "lucide-react";

/**
 * La mappa dell'area operativa: i comuni gestiti, colorati per committente e
 * contratto, con i confini ufficiali.
 *
 * La mappa e' un file HTML unico generato fuori dall'app (progetto «mappa»
 * sul disco di Riccardo, dal foglio ELENCO COMUNI GESTITI SGM). Qui la si
 * guarda dentro un riquadro, si scarica la tavola A3 e, chi puo', carica la
 * versione rigenerata. Il file arriva con il token e finisce nel riquadro
 * come blob: non e' un indirizzo aperto a chi non e' collegato.
 */
const dataIt = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "");

export default function AreaOperativa() {
  const { user } = useAuth();
  const puoCaricare = ["admin", "amministrativo"].includes(user?.role);
  const [info, setInfo] = useState(null);
  const [src, setSrc] = useState(null);
  const [errore, setErrore] = useState(null);
  const [carica, setCarica] = useState(false);
  const [pieno, setPieno] = useState(false);
  const riquadro = useRef(null);

  const leggi = async () => {
    try {
      const { data } = await api.get("/mappa");
      setInfo(data);
      if (!data.html) { setErrore("La mappa non è ancora stata caricata."); return; }
      // La data dell'ultimo caricamento finisce nell'indirizzo: cosi' una
      // mappa nuova e' un indirizzo nuovo, e nessun browser puo' servire la
      // copia di prima credendo di fare un favore.
      const quando = encodeURIComponent(data.aggiornata_il || "");
      const r = await authFetch(`/mappa/html?v=${quando}`);
      if (!r.ok) throw new Error();
      const testo = await r.text();
      setSrc((vecchio) => { if (vecchio) URL.revokeObjectURL(vecchio); return URL.createObjectURL(new Blob([testo], { type: "text/html;charset=utf-8" })); });
    } catch { setErrore("Errore nel caricamento della mappa"); }
  };
  useEffect(() => { leggi(); return () => { if (src) URL.revokeObjectURL(src); }; /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  // Le tavole da stampare sono due: i comuni con i contratti e gli impianti
  // dove si scarica. La mappa che si guarda a schermo invece e' una sola.
  const scaricaPdf = async (tavola, nomeFile) => {
    try {
      const r = await authFetch(
        `/mappa/pdf?tavola=${tavola}&v=${encodeURIComponent(info?.aggiornata_il || "")}`);
      if (!r.ok) throw new Error();
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement("a"); a.href = url; a.download = nomeFile;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Tavola in PDF non disponibile"); }
  };

  const schermoIntero = () => {
    const el = riquadro.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => setPieno(true));
    else setPieno(true);
  };

  return (
    <div className="space-y-4" data-testid="area-operativa">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Cantieri</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Area operativa</h1>
          <p className="text-sm text-slate-500 mt-1">
            I comuni gestiti, colorati per committente e contratto, con i confini ufficiali.
            {info?.comuni ? ` ${info.comuni} righe dell'elenco comuni.` : ""}
            {info?.aggiornata_il ? ` Aggiornata il ${dataIt(info.aggiornata_il)}.` : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={schermoIntero} className="h-11" disabled={!src}>
            <Maximize2 className="w-4 h-4 mr-2" /> A tutto schermo
          </Button>
          {info?.pdf && (
            <Button variant="outline" className="h-11" data-testid="mappa-pdf"
                    onClick={() => scaricaPdf("comuni", "Area operativa SGM.pdf")}>
              <Download className="w-4 h-4 mr-2" /> Tavola comuni (PDF)
            </Button>
          )}
          {info?.pdf_impianti && (
            <Button variant="outline" className="h-11" data-testid="mappa-pdf-impianti"
                    onClick={() => scaricaPdf("impianti", "Impianti e cave SGM.pdf")}>
              <Download className="w-4 h-4 mr-2" /> Tavola impianti (PDF)
            </Button>
          )}
          {puoCaricare && (
            <Button onClick={() => setCarica(true)} className="bg-orange-500 hover:bg-orange-600 text-white h-11"
                    data-testid="mappa-carica">
              <Upload className="w-4 h-4 mr-2" /> Aggiorna la mappa
            </Button>
          )}
        </div>
      </div>

      {errore && !src && (
        <Card className="border-dashed border-2"><CardContent className="p-10 text-center text-slate-500">{errore}</CardContent></Card>
      )}
      {!errore && !src && (
        <p className="text-sm text-slate-500 py-10 text-center flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Caricamento della mappa...
        </p>
      )}
      {src && (
        <div ref={riquadro}
             className={pieno ? "fixed inset-0 z-[80] bg-white" : "rounded-lg border border-slate-200 overflow-hidden bg-white"}
             style={pieno ? undefined : { height: "calc(100vh - 230px)", minHeight: 520 }}>
          {/* Chiudi in basso a destra: in alto ci sono i pulsanti di zoom della mappa. */}
          {pieno && (
            <button type="button" onClick={() => setPieno(false)}
                    className="absolute bottom-4 right-4 z-10 bg-orange-500 text-white rounded-full px-4 py-2 text-sm font-semibold shadow-lg">
              Chiudi
            </button>
          )}
          <iframe title="Area operativa SGM" src={src} className="w-full h-full border-0" />
        </div>
      )}

      {carica && <DialogCarica onChiudi={() => setCarica(false)} onFatto={() => { setCarica(false); leggi(); }} />}
    </div>
  );
}

function DialogCarica({ onChiudi, onFatto }) {
  const [html, setHtml] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [pdfImpianti, setPdfImpianti] = useState(null);
  const [lavoro, setLavoro] = useState(false);
  const manda = async () => {
    if (!html && !pdf && !pdfImpianti) { toast.error("Scegli almeno il file mappa_sgm.html"); return; }
    setLavoro(true);
    try {
      const fd = new FormData();
      if (html) fd.append("html", html);
      if (pdf) fd.append("pdf", pdf);
      if (pdfImpianti) fd.append("pdf_impianti", pdfImpianti);
      const { data } = await api.post("/mappa", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.comuni ? `Mappa aggiornata: ${data.comuni} righe` : "Mappa aggiornata");
      onFatto();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel caricamento"); }
    finally { setLavoro(false); }
  };
  return (
    <Dialog open onOpenChange={onChiudi}>
      <DialogContent>
        <DialogHeader><DialogTitle>Aggiorna la mappa</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">
            Carica qui i file prodotti dal progetto della mappa. Basta <b>mappa_sgm.html</b>:
            è un file unico, dentro ci sono già comuni e impianti. Le due tavole in PDF
            servono solo per stampare, e si possono caricare anche in un secondo momento.
          </p>
          <div><Label>mappa_sgm.html — la mappa</Label>
            <input type="file" accept=".html" onChange={(e) => setHtml(e.target.files?.[0] || null)} className="block mt-1 text-sm" /></div>
          <div><Label>mappa_sgm_stampa.pdf — tavola dei comuni (facoltativo)</Label>
            <input type="file" accept=".pdf" onChange={(e) => setPdf(e.target.files?.[0] || null)} className="block mt-1 text-sm" /></div>
          <div><Label>impianti_stampa.pdf — tavola degli impianti (facoltativo)</Label>
            <input type="file" accept=".pdf" data-testid="carica-pdf-impianti"
                   onChange={(e) => setPdfImpianti(e.target.files?.[0] || null)} className="block mt-1 text-sm" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi}>Annulla</Button>
          <Button onClick={manda} disabled={lavoro} className="bg-orange-500 hover:bg-orange-600 text-white">
            {lavoro ? "Caricamento..." : "Carica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
